/**
 * Pitch-class-set utilities.
 *
 * A pitch-class set is the matching key for the whole application: detections are
 * compared to suggestions here, and nowhere else. Sets are always normalised —
 * sorted ascending and deduped — before comparison, so callers never have to care
 * what order they built an array in.
 */

import type { PitchClass } from './types.js';

/** Sort ascending and dedupe. Idempotent. */
export function normalizePcSet(pcs: readonly PitchClass[]): PitchClass[] {
  return [...new Set(pcs)].sort((a, b) => a - b);
}

/** Set equality, order- and duplicate-insensitive. */
export function pcSetEquals(a: readonly PitchClass[], b: readonly PitchClass[]): boolean {
  const left = normalizePcSet(a);
  const right = normalizePcSet(b);
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

/** True when `set` contains `pc`. */
export function pcSetContains(set: readonly PitchClass[], pc: PitchClass): boolean {
  return set.includes(pc);
}

/** True when every member of `subset` is in `superset` (not necessarily strictly). */
export function pcSetIsSuperset(
  superset: readonly PitchClass[],
  subset: readonly PitchClass[],
): boolean {
  const big = new Set(superset);
  for (const pc of subset) {
    if (!big.has(pc)) return false;
  }
  return true;
}

/**
 * True when `superset` contains everything in `subset` AND at least one pitch class more.
 * This is the test the session reducer uses for the arpeggio case: playing C then G then E
 * then B♭ within `arpeggioWindowMs` should grow the current chord in place rather than
 * appending four nodes.
 */
export function pcSetIsStrictSuperset(
  superset: readonly PitchClass[],
  subset: readonly PitchClass[],
): boolean {
  const big = normalizePcSet(superset);
  const small = normalizePcSet(subset);
  return big.length > small.length && pcSetIsSuperset(big, small);
}

/** Transpose every member by `semitones`. */
export function transposePcSet(pcs: readonly PitchClass[], semitones: number): PitchClass[] {
  return normalizePcSet(pcs.map((pc) => ((((pc + semitones) % 12) + 12) % 12) as PitchClass));
}

/** A stable string key for using pitch-class sets as `Map`/`Record` keys. */
export function pcSetKey(pcs: readonly PitchClass[]): string {
  return normalizePcSet(pcs).join(',');
}

/** A 12-bit mask of the set, useful for fast template comparison in the recogniser. */
export function pcSetToBitmask(pcs: readonly PitchClass[]): number {
  let mask = 0;
  for (const pc of pcs) mask |= 1 << pc;
  return mask;
}

/**
 * Reduce a 12-bin chroma vector to the set of pitch classes that are actually sounding.
 *
 * `threshold` is **relative to the strongest bin**, in 0..1 — 0.5 means "at least half as
 * loud as the loudest pitch class". A relative threshold is what makes this usable across
 * a room-dependent dynamic range without recalibration; an absolute one is not.
 *
 * An all-zero (or negative-only) chroma yields an empty set rather than twelve members.
 */
export function pcSetFromChroma(chroma: readonly number[], threshold: number): PitchClass[] {
  if (chroma.length !== 12) {
    throw new RangeError(`pcSetFromChroma expects 12 bins, received ${chroma.length}`);
  }
  let max = 0;
  for (const value of chroma) {
    if (Number.isFinite(value) && value > max) max = value;
  }
  if (max <= 0) return [];

  const cutoff = max * threshold;
  const out: PitchClass[] = [];
  for (let pc = 0; pc < 12; pc += 1) {
    const value = chroma[pc] as number;
    if (Number.isFinite(value) && value >= cutoff) out.push(pc as PitchClass);
  }
  return out;
}
